"use client";

import type { PlaceholderValue } from "@homeinstead/schemas";
import type { PortableTextComponents } from "@portabletext/react";
import type { PortableTextBlock } from "@portabletext/types";
import type { HeadingFont, RichTextComponentsFactory } from "./types";

import { PortableText } from "@portabletext/react";
import { useMemo } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTriggerPlusMinus,
} from "../Accordion";
import { clampListItemLevels } from "./clamp-list-item-levels";

type RichTextAccordionItem = {
  _key: string;
  content?: PortableTextBlock[];
  title?: string;
};

type RichTextAccordionValue = {
  items?: RichTextAccordionItem[];
  title?: string;
};

type RichTextAccordionProps = {
  activePlaceholderKeys: readonly string[];
  components?: Partial<PortableTextComponents>;
  createRichTextComponents: RichTextComponentsFactory;
  headingFont?: HeadingFont;
  onLinkClick?: (href: string) => void;
  placeholders: Record<string, PlaceholderValue>;
  value: RichTextAccordionValue;
};

function RichTextAccordion({
  activePlaceholderKeys,
  components,
  createRichTextComponents,
  headingFont,
  onLinkClick,
  placeholders,
  value: { items, title },
}: RichTextAccordionProps) {
  const itemComponents = useMemo(
    () =>
      createRichTextComponents({
        activePlaceholderKeys,
        components,
        headingFont,
        includeAccordion: false,
        includeColumns: false,
        onLinkClick,
        placeholders,
      }),
    [
      activePlaceholderKeys,
      components,
      createRichTextComponents,
      headingFont,
      onLinkClick,
      placeholders,
    ],
  );

  if (!Array.isArray(items) || items.length < 1) {
    return null;
  }

  return (
    <div className="my-6">
      {!title ? null : (
        <h3 className="mb-6 font-editorial-new font-light text-1xl">{title}</h3>
      )}

      <Accordion collapsible type="single">
        {items.map((item) => (
          <AccordionItem key={item._key} value={item._key}>
            <AccordionTriggerPlusMinus className="gap-x-8 text-left text-md">
              {item.title}
            </AccordionTriggerPlusMinus>
            <AccordionContent
              className="group-data-[state=closed]:hidden"
              forceMount
            >
              <PortableText
                components={itemComponents}
                value={clampListItemLevels(item.content ?? [])}
              />
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}

export { RichTextAccordion };
